import siteConfig from "./siteConfig.json";

const homeStatsConfig = siteConfig.statistics?.home ?? {};

export const navLinks = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Contact Us", to: "/contact" }
];

export const studyDestinations = [
  { name: "USA", slug: "usa", flag: "🇺🇸" },
  { name: "Canada", slug: "canada", flag: "🇨🇦" },
  { name: "UK", slug: "uk", flag: "🇬🇧" },
  { name: "Australia", slug: "australia", flag: "🇦🇺" },
  { name: "UAE", slug: "uae", flag: "🇦🇪" },
  { name: "New Zealand", slug: "new-zealand", flag: "🇳🇿" },
  { name: "Europe", slug: "europe", flag: "🇪🇺" },
  { name: "Ireland", slug: "ireland", flag: "🇮🇪" },
  { name: "Germany", slug: "germany", flag: "🇩🇪" }
];

export const heroContent = {
  title: "Study Abroad With Clarity and Confidence",
  subtitle:
    "No guesswork. No confusion. We help you choose the right destination, shortlist the right universities, and complete admissions plus visa steps with expert support.",
  supportPoints: [
    "Clear country and course roadmap based on your profile",
    "Smart shortlist with transparent admission expectations",
    "Step-by-step application and visa documentation support"
  ],
  primaryCta: {
    label: "Book Free Counselling",
    to: siteConfig.links.bookCounselling
  },
  secondaryCta: {
    label: "Explore Destinations",
    to: siteConfig.links.exploreDestinations
  },
  floatingCards: ["Profile Review", "University Shortlisting", "Visa Readiness"],
  quickTrustPoints: homeStatsConfig.quickTrustPoints ?? [
    { label: "Focused counselling", value: "1:1" },
    { label: "Global destinations", value: "9+" },
    { label: "Guided process", value: "End-to-end" }
  ],
  heroImage: siteConfig.images.hero
};

export const introFeatures = [
  {
    title: "Country and Course Mapping",
    description:
      "Match your profile, goals, and budget with the destinations and programs that suit you best.",
    icon: "globe"
  },
  {
    title: "University Shortlisting",
    description:
      "Build a smart, balanced list of universities with realistic outcomes and strong long-term value.",
    icon: "target"
  },
  {
    title: "Application Assistance",
    description:
      "Craft stronger applications with clear documentation, timeline support, and personal guidance.",
    icon: "file"
  },
  {
    title: "Documentation Expertise",
    description:
      "Stay organized with structured checklists and document review at every critical stage.",
    icon: "folder"
  },
  {
    title: "Visa Guidance",
    description:
      "Receive practical interview prep and visa process support with complete transparency.",
    icon: "shield"
  }
];

export const stats = homeStatsConfig.mainStats ?? [
  { label: "Destinations", value: 9, suffix: "+" },
  { label: "Years of Experience", value: 9, suffix: "+" },
  { label: "Happy Students", value: 1200, suffix: "+" },
  { label: "Guidance Sessions", value: 5000, suffix: "+" }
];

export const destinations = [
  {
    name: "USA",
    slug: "usa",
    flag: "🇺🇸",
    hook: "Top-ranked research universities and future-ready career pathways.",
    image: siteConfig.images.destinations.usa
  },
  {
    name: "Canada",
    slug: "canada",
    flag: "🇨🇦",
    hook: "High-quality education, welcoming communities, and strong career pathways.",
    image: siteConfig.images.destinations.canada
  },
  {
    name: "UK",
    slug: "uk",
    flag: "🇬🇧",
    hook: "Globally respected degrees with rich academic heritage and opportunities.",
    image: siteConfig.images.destinations.uk
  },
  {
    name: "Australia",
    slug: "australia",
    flag: "🇦🇺",
    hook: "Student-friendly campuses, practical learning, and vibrant city life.",
    image: siteConfig.images.destinations.australia
  },
  {
    name: "UAE",
    slug: "uae",
    flag: "🇦🇪",
    hook: "Global campuses, modern cities, and fast-growing career opportunities.",
    image: siteConfig.images.destinations.uae
  },
  {
    name: "New Zealand",
    slug: "new-zealand",
    flag: "🇳🇿",
    hook: "Quality education, safe communities, and a balanced student lifestyle.",
    image: siteConfig.images.destinations.newZealand
  },
  {
    name: "Europe",
    slug: "europe",
    flag: "🇪🇺",
    hook: "Diverse programs across leading education hubs and cultural depth.",
    image: siteConfig.images.destinations.europe
  },
  {
    name: "Ireland",
    slug: "ireland",
    flag: "🇮🇪",
    hook: "Fast-growing innovation ecosystem with welcoming academic communities.",
    image: siteConfig.images.destinations.ireland
  },
  {
    name: "Germany",
    slug: "germany",
    flag: "🇩🇪",
    hook: "Strong engineering excellence and high-quality education outcomes.",
    image: siteConfig.images.destinations.germany
  }
];

export const servicesProvided = [
  {
    title: "Student Visa",
    summary: "Documentation guidance, financial checklist support, and interview readiness for study pathways.",
    icon: "student"
  },
  {
    title: "Work Visa",
    summary: "Profile-based support for eligible skilled pathways and process planning.",
    icon: "work"
  },
  {
    title: "Tourist Visa",
    summary: "Clear checklist support for short-stay travel documentation and application sequencing.",
    icon: "tourist"
  },
  {
    title: "Dependent / Spouse Visa",
    summary: "Assistance with relationship proof, financial documents, and supporting paperwork flow.",
    icon: "dependent"
  },
  {
    title: "Visitor Visa",
    summary: "Guidance for family visits, invitation documentation, and application preparation.",
    icon: "visitor"
  },
  {
    title: "Business Visa",
    summary: "Structured support for travel purpose documentation, itinerary, and compliance checks.",
    icon: "business"
  }
];

export const processSteps = [
  {
    title: "Admission Counselling",
    description:
      "We begin with a deep profile discussion so your path is shaped by your goals, strengths, and aspirations.",
    icon: "counselling"
  },
  {
    title: "University Selection",
    description:
      "Our advisors build a strategic shortlist that balances ambition, fit, and long-term outcomes.",
    icon: "selection"
  },
  {
    title: "Application Assistance",
    description:
      "From SOP planning to timeline management, we help present your story with clarity and confidence.",
    icon: "application"
  },
  {
    title: "Visa Guidance",
    description:
      "You receive structured support for documentation, interview preparation, and final decision readiness.",
    icon: "visa"
  }
];

export const whyChooseUs = [
  {
    title: "Personalized Guidance",
    description:
      "Every student receives tailored advice built around profile, destination goals, and family priorities.",
    icon: "user"
  },
  {
    title: "Smart University Shortlisting",
    description:
      "Data-backed shortlists designed for both admissions competitiveness and career trajectory.",
    icon: "chart"
  },
  {
    title: "Transparent Process",
    description:
      "Clear timelines, honest expectations, and regular progress updates at every step.",
    icon: "shield"
  },
  {
    title: "Dedicated Support",
    description:
      "A committed advisor who stays accessible throughout counselling, applications, and decision stages.",
    icon: "support"
  },
  {
    title: "Documentation Expertise",
    description:
      "Meticulous document review workflows to reduce errors and keep submissions application-ready.",
    icon: "docs"
  },
  {
    title: "Student-first Mentorship",
    description:
      "We combine strategic admissions guidance with human mentorship so you feel supported, not rushed.",
    icon: "mentorship"
  }
];

export const finalCta = {
  title: "Your Next Chapter Deserves Expert Guidance",
  subtitle:
    "Book a personalized counselling session with NextEra Abroad and move forward with strategy, confidence, and trusted support.",
  primary: {
    label: "Book Free Counselling",
    to: siteConfig.links.bookCounselling
  },
  secondary: {
    label: "Talk to an Expert",
    to: siteConfig.links.talkToExpert
  }
};

export const footerData = {
  quickLinks: [
    { label: "Home", to: "/" },
    { label: "About Us", to: "/about" },
    { label: "Testimonials", to: "/testimonials" },
    { label: "Contact Us", to: "/contact" }
  ],
  destinationLinks: studyDestinations,
  socialLinks: [
    { label: "Instagram", to: siteConfig.social.instagram },
    { label: "LinkedIn", to: siteConfig.social.linkedin },
    { label: "Facebook", to: siteConfig.social.facebook }
  ]
};
